import Vue from 'vue'
import Vuex, { Module, StoreOptions } from 'vuex'
import axios from 'axios'
import { BaseAccessLevel, BaseRequestMethod, BaseServerControllers, BaseServerModules, BaseUserModule } from '@/types'
import { loadModuleConfigs } from '@/utils/config-util'
import { getEnumOrdinal } from '@/utils/enum-util'

type StoreConfig = Module<any, any>

interface ServerData {
  moduleName: string
  controllerName: string
  requestPath: string
  requestSubpath: string
  requestMethods: BaseRequestMethod[]
}

export default class BaseVuexStore<S> extends Vuex.Store<S> {
  private currentModuleName: string | null
  private currentAccessLevel: BaseAccessLevel | null
  private moduleNames: string[]
  private serverModules: BaseServerModules

  constructor(storeOptions: StoreOptions<S> = {}) {
    storeOptions.strict = (process.env.NODE_ENV !== 'production')

    super(storeOptions)

    this.currentModuleName = null
    this.currentAccessLevel = null
    this.moduleNames = []
    this.serverModules = {}
  }

  get $servers(): BaseServerModules {
    return this.serverModules
  }

  setDefaultModuleNames(defaultModuleNames: string[]): void {
    this.loadModuleStores(defaultModuleNames)
  }

  setUserModules(userModules: BaseUserModule[]): void {
    this.loadModuleStores(userModules.map(({ moduleName }) => moduleName))

    this.generateDynamicServers(userModules)
  }

  setUserModule(userModule: BaseUserModule): void {
    this.currentModuleName = userModule.moduleName
    this.currentAccessLevel = userModule.accessLevel
  }

  private loadModuleStores(moduleNames: string[]): void {
    loadModuleConfigs<StoreConfig>('/store', moduleNames, ({ moduleName, moduleValue }) => {
      this.replaceStore(moduleValue, moduleName)
    })
  }

  private replaceStore(store: StoreConfig, moduleName: string): void {
    store.namespaced = true

    if (this.moduleNames.includes(moduleName)) this.unregisterModule(moduleName)

    this.registerModule(moduleName, store)

    this.moduleNames.push(moduleName)
  }

  private generateDynamicServers(userModules: BaseUserModule[]): void {
    userModules.forEach((userModule) => {
      const { moduleName, controllers } = userModule

      let serverControllers = this.serverModules[moduleName]

      controllers.forEach((controller) => {
        const { controllerName, requestPath, requests } = controller

        requests.forEach((request) => {
          const requestSubpath = request.subpath
          const requestMethods = request.methods

          serverControllers = serverControllers ?? {}

          this.mergeServers(serverControllers, {
            moduleName,
            controllerName,
            requestPath,
            requestSubpath,
            requestMethods
          })
        })
      })

      if (serverControllers) this.serverModules[moduleName] = serverControllers
    })
  }

  private mergeServers(serverControllers: BaseServerControllers, serverData: ServerData): void {
    const { moduleName, controllerName, requestPath: controllerRequestPath, requestSubpath, requestMethods } = serverData
    const requestPath = `${controllerRequestPath}${requestSubpath}`

    let serverRequests = serverControllers[controllerName]
    let serverRequestMethods = serverRequests?.[requestSubpath]

    requestMethods?.forEach((requestMethod) => {
      const { methodName, accessLevel, defaultSize, defaultPage } = requestMethod

      const checkAccess = (): boolean => {
        return (moduleName === this.currentModuleName) &&
          (getEnumOrdinal(BaseAccessLevel, accessLevel ?? null) <= getEnumOrdinal(BaseAccessLevel, this.currentAccessLevel))
      }

      serverRequestMethods = serverRequestMethods ?? {}

      switch (methodName) {
        case 'GET_PAGE': {
          serverRequestMethods.getPage = {
            execute: async (requestConfig) => {
              const { fallbackDataCallback, fallbackStorageKey } = requestConfig ?? {}

              try {
                const { data } = await axios.get(requestPath, requestConfig)

                if (fallbackDataCallback && fallbackStorageKey) {
                  if (data?.content?.length) localStorage.setItem(fallbackStorageKey, JSON.stringify(data))
                  else localStorage.removeItem(fallbackStorageKey)
                }

                return data
              }
              catch (error: any) {
                if (fallbackDataCallback && fallbackStorageKey) {
                  fallbackDataCallback(JSON.parse(localStorage.getItem(fallbackStorageKey) as string) ?? {})
                }

                throw error
              }
            },
            checkAccess,
            defaultSize,
            defaultPage
          }

          break
        }
        case 'GET': {
          serverRequestMethods.get = {
            execute: async (requestConfig) => {
              const { data } = await axios.get(requestPath, requestConfig)

              return data
            },
            checkAccess
          }

          break
        }
        case 'POST': {
          serverRequestMethods.post = {
            execute: async (requestData, requestConfig) => {
              const { data } = await axios.post(requestPath, requestData, requestConfig)

              return data
            },
            checkAccess
          }

          break
        }
        case 'PUT': {
          serverRequestMethods.put = {
            execute: async (requestData, requestConfig) => {
              const { data } = await axios.put(requestPath, requestData, requestConfig)

              return data
            },
            checkAccess
          }

          break
        }
        case 'DELETE': {
          serverRequestMethods.delete = {
            execute: async (requestConfig) => {
              const { data } = await axios.delete(requestPath, requestConfig)

              return data
            },
            checkAccess
          }

          break
        }
      }
    })

    if (serverRequestMethods) {
      serverRequests = serverRequests ?? {}

      serverRequests[requestSubpath] = serverRequestMethods
      serverControllers[controllerName] = serverRequests
    }
  }
}

Vue.use(Vuex)

export const baseVuexStore = new BaseVuexStore()

declare module 'vue/types/vue' {
  interface Vue {
    readonly $servers: typeof BaseVuexStore.prototype.$servers
  }
}

Vue.prototype.$servers = baseVuexStore.$servers